import axios from "axios";
import { getAuthHeaders } from "./auth";

const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:7071/api";

export type CycleStatus = "Stage1" | "Stage2" | "PostProcessing" | "Completed" | "SkippedNoMembers";

export interface CycleSummary {
  id: number;
  toolName: string;
  controlId: string;
  cycleName: string;
  status: CycleStatus;
  stage1StartedAt: string | null;
  stage1Deadline: string | null;
  s1ReviewerUpn: string | null;
  stage2StartedAt: string | null;
  stage2Deadline: string | null;
  s2ReviewerUpn: string | null;
  total: number;
  reviewed: number;
  escalated: number;
  s2Reviewed: number;
}

export interface ReviewItem {
  id: number;
  memberUpn: string;
  memberDisplayName: string;
  memberObjectId: string | null;
  groupName: string;
  groupObjectId: string;
  teamName: string | null;
  dateAdded: string | null;
  accountDisabled: boolean;
  onLongTermLeave: boolean;
  isSelfReview: boolean;
  s1Decision: string | null;
  s1Justification: string | null;
  s1ReviewedBy: string | null;
  s1ReviewedDate: string | null;
  s2Decision: string | null;
  s2Justification: string | null;
  s2ReviewedBy: string | null;
  s2ReviewedDate: string | null;
}

export interface CycleDetail {
  cycle: CycleSummary;
  items: ReviewItem[];
}

export interface PatchCyclePayload {
  stage1Deadline?: string;
  stage2Deadline?: string;
}

export interface PatchCycleResponse {
  success: boolean;
  cycleId: number;
  soxCeiling: string;
  extensions: Array<{
    stage: 1 | 2;
    oldDeadline: string | null;
    newDeadline: string;
    eventRaised: boolean;
    eventError: string | null;
  }>;
}

export interface BulkExtendPlanRow {
  cycleId: number;
  toolName: string;
  cycleName: string;
  status: "Stage1" | "Stage2";
  activeStage: 1 | 2;
  currentDeadline: string;
  /** Proposed new deadline = currentDeadline + days (no clamping). */
  newDeadline: string;
  /** SOX cadence ceiling (last day of trigger month in Berlin) — informational
   *  only. UI shows a red advisory warning when newDeadline > soxCeiling. */
  soxCeiling: string;
  /** True if newDeadline > soxCeiling. UI surfaces a red "please consult
   *  Internal Audit" warning for these rows. The extension still happens —
   *  the backend trusts the admin to have had that conversation already. */
  beyondTriggerMonth: boolean;
  hasOrchestrationInstance: boolean;
}

export interface BulkExtendPreviewResponse {
  days: number;
  totalCycles: number;
  /** Count of cycles whose newDeadline lands past their trigger month. */
  beyondTriggerMonthCycles: number;
  plan: BulkExtendPlanRow[];
}

export interface BulkExtendCommitResponse {
  applied: number;
  days: number;
  totalCycles: number;
  beyondTriggerMonthCycles: number;
  eventsRaised: number;
  eventsFailed: number;
  eventsSkipped: number;
  plan: BulkExtendPlanRow[];
}

/**
 * Body for POST /api/manage/cycles/{cycleId}/reassign-s2.
 *
 * Validates server-side that:
 *  - the cycle exists and is in Stage2 status
 *  - newReviewerUpn is email-shaped and exists in Entra (Graph 404 → rejected)
 *  - newReviewerUpn differs from the current s2_reviewer_upn (no-op rejected)
 *
 * `reason` is optional (Decision 8). When provided it's persisted in the
 * cycle_notes row's metadata_json and shown to both old and new reviewer
 * in their Slack DMs.
 */
export interface ReassignS2Payload {
  newReviewerUpn: string;
  reason?: string;
}

/**
 * Response from POST .../reassign-s2 on a successful reassignment (HTTP 200).
 *
 * The reassignment itself (s2_reviewer_upn UPDATE + cycle_notes INSERT) runs
 * inside a SQL transaction — either both commit or both roll back. Anything
 * after (reminderLoop cancel/restart, Slack DMs) is best-effort and recorded
 * in `issues` if it fails; the reassignment is still considered successful.
 */
export interface ReassignS2Response {
  success: true;
  cycleId: number;
  oldReviewerUpn: string;
  newReviewerUpn: string;
  newReviewerDisplayName: string;
  newReviewerAccountEnabled: boolean;
  newReviewerSlackId: string | null;
  reminderInstanceId: string | null;
  /** Best-effort step failures (empty array on a fully clean reassignment).
   *  Caller surfaces these in the UI so admins can follow up manually — e.g.
   *  Slack DM didn't go because the new reviewer has no Slack ID. */
  issues: string[];
}

export async function listCycles(scope: "active" | "past" = "active"): Promise<CycleSummary[]> {
  const resp = await axios.get(
    `${BASE_URL}/manage/cycles?scope=${scope}`,
    { headers: getAuthHeaders() }
  );
  return resp.data.cycles as CycleSummary[];
}

/** @deprecated Prefer listCycles("active"). Kept for backward compatibility. */
export async function listActiveCycles(): Promise<CycleSummary[]> {
  return listCycles("active");
}

export async function getCycleDrilldown(cycleId: number): Promise<CycleDetail> {
  const resp = await axios.get(`${BASE_URL}/manage/cycles/${cycleId}`, { headers: getAuthHeaders() });
  return resp.data as CycleDetail;
}

/**
 * Download (regenerate-from-DB) the evidence Excel for a cycle. Returns the
 * file as a Blob plus the server-provided filename (parsed from the
 * Content-Disposition header). The caller triggers the browser download.
 * Used by the Past section of the Review Cycles page — the recovery path when
 * the emailed copy and the SharePoint archive are both gone.
 */
export async function exportCycleExcel(cycleId: number): Promise<{ blob: Blob; fileName: string }> {
  const resp = await axios.get(`${BASE_URL}/manage/cycles/${cycleId}/export`, {
    headers: getAuthHeaders(),
    responseType: "blob",
  });
  const disposition = (resp.headers["content-disposition"] ?? resp.headers["Content-Disposition"] ?? "") as string;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : `cycle-${cycleId}.xlsx`;
  return { blob: resp.data as Blob, fileName };
}

/**
 * PATCH a cycle's deadlines.
 *
 * Phase 4.6 semantics: refuses earlier-than-current deadlines (no force-freeze)
 * and refuses extending the wrong stage's deadline given the current status,
 * but DOES NOT reject deadlines past the SOX cadence ceiling — the ceiling is
 * returned for the UI to display a red advisory warning, while the change
 * itself is allowed. On success, raises the matching DeadlineExtended event
 * onto the running orchestration so its freeze timer is re-armed.
 *
 * Throws on 4xx with axios, so callers should surface the server message
 * (see AdminCycleDetailPage's errorMessageFromAxios).
 */
export async function patchCycle(cycleId: number, payload: PatchCyclePayload): Promise<PatchCycleResponse> {
  const resp = await axios.patch(
    `${BASE_URL}/manage/cycles/${cycleId}`,
    payload,
    { headers: getAuthHeaders() }
  );
  return resp.data as PatchCycleResponse;
}

/**
 * Preview a bulk extension (read-only) — returns one row per active cycle
 * with the proposed new deadline (current + days, no clamping) and a
 * `beyondTriggerMonth` flag the UI surfaces as a red advisory warning.
 */
export async function bulkExtendPreview(days: number): Promise<BulkExtendPreviewResponse> {
  const resp = await axios.get(
    `${BASE_URL}/manage/cycles/bulk-extend-preview?days=${encodeURIComponent(days)}`,
    { headers: getAuthHeaders() }
  );
  return resp.data as BulkExtendPreviewResponse;
}

/**
 * Commit a bulk extension across all active Stage1/Stage2 cycles. Each
 * cycle's deadline is set to current + days (no clamping). Audit-log rows
 * are written per cycle, noting whether the result lands past the trigger
 * month for SOX evidence.
 */
export async function bulkExtendCommit(days: number): Promise<BulkExtendCommitResponse> {
  const resp = await axios.post(
    `${BASE_URL}/manage/cycles/bulk-extend`,
    { days },
    { headers: getAuthHeaders() }
  );
  return resp.data as BulkExtendCommitResponse;
}

/**
 * Reassign the Stage 2 reviewer of an in-flight cycle. Admin-only. Atomic
 * SQL transaction wraps the cycle UPDATE and cycle_notes INSERT — audit
 * row is guaranteed to land with the reassignment. Cancels the running
 * reminderLoop targeting the old reviewer and starts a new one targeting
 * the new reviewer. Sends Slack DMs to both reviewers (paste-ready admin
 * alert if the new reviewer has no Slack ID).
 *
 * Backend rejects (HTTP 400/404) on: malformed UPN, non-existent UPN,
 * non-Stage2 cycle, identical-to-current UPN, missing cycle. Throws on 4xx
 * with axios; callers should surface the server error message.
 */
export async function reassignS2(cycleId: number, payload: ReassignS2Payload): Promise<ReassignS2Response> {
  const resp = await axios.post(
    `${BASE_URL}/manage/cycles/${cycleId}/reassign-s2`,
    payload,
    { headers: getAuthHeaders() }
  );
  return resp.data as ReassignS2Response;
}
